'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import { FaqContext } from './FaqProvider';
import { LINE_DATA } from './FaqList.helpers';

interface Faq {
  slug: string;
  frontmatter: {
    question: string;
  };
  content: React.ReactNode;
}

interface Props {
  data: Array<Faq>;
}

function FaqList({ data }: Props) {
  const { openItems, relevantMatches, toggleItem } =
    React.useContext(FaqContext);

  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <ListWrapper data-id="faq-list">
      {data.map((faq) => (
        <FaqItem
          key={faq.slug}
          {...faq}
          isOpen={!!openItems[faq.slug]}
          isHighlighted={relevantMatches.includes(faq.slug)}
          prefersReducedMotion={prefersReducedMotion}
          handleToggle={toggleItem}
        />
      ))}
    </ListWrapper>
  );
}

interface FaqItemProps extends Faq {
  isOpen: boolean;
  isHighlighted: boolean;
  prefersReducedMotion: boolean;
  handleToggle: (slug: string) => void;
}

const FaqItem = React.memo(function FaqItem({
  slug,
  frontmatter,
  content,
  isOpen,
  isHighlighted,
  prefersReducedMotion,
  handleToggle,
}: FaqItemProps) {
  const [isPressed, setIsPressed] = React.useState(false);

  const detailsRef = React.useRef<HTMLDetailsElement>(null);

  const leftPoints = isPressed
    ? LINE_DATA[0].flat
    : isOpen
      ? LINE_DATA[0].chevron
      : LINE_DATA[0].plus;
  const rightPoints = isPressed
    ? LINE_DATA[1].flat
    : isOpen
      ? LINE_DATA[1].chevron
      : LINE_DATA[1].plus;
  const leftLineSpring = useSpring({
    ...leftPoints,
    config: {
      tension: 300,
      friction: 10,
      clamp: true,
    },
    immediate: prefersReducedMotion,
  });
  const rightLineSpring = useSpring({
    ...rightPoints,
    config: {
      tension: 300,
      friction: 10,
      clamp: true,
    },
    immediate: prefersReducedMotion,
  });

  return (
    <Wrapper
      ref={detailsRef}
      open={isOpen}
      data-faq-slug={slug}
      id={`faq-${slug}`}
      data-is-highlighted={String(isHighlighted)}
      onClick={(event) => {
        // Allow link clicks
        // TS doesn't like it, but I don't care
        // @ts-expect-error
        const isInsideAnchor = !!event.target.closest('a');
        if (
          event.target instanceof HTMLAnchorElement ||
          isInsideAnchor
        ) {
          return;
        }

        event.preventDefault();
        handleToggle(slug);
      }}
    >
      <Question
        onMouseDown={() => {
          setIsPressed(true);
        }}
        onMouseUp={() => {
          setIsPressed(false);
        }}
      >
        <IconWrapper>
          <Background />
          <Svg
            xmlns="http://www.w3.org/2000/svg"
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <animated.line {...leftLineSpring} />
            <animated.line {...rightLineSpring} />
          </Svg>
        </IconWrapper>
        <QuestionText>{frontmatter.question}</QuestionText>
      </Question>
      <Answer data-status={isOpen ? 'open' : 'closed'}>
        {content}
      </Answer>
    </Wrapper>
  );
});

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Wrapper = styled.details`
  position: relative;
  list-style: none;
  container-type: inline-size;
  scroll-margin-top: 24px;

  &[data-is-highlighted='true'] {
    --selection-background-color: hsl(50deg 100% 78% / 0.75);
  }
`;

const Question = styled.summary`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  outline-color: var(--color-primary);
  outline-offset: 8px;
  font-weight: var(--font-weight-medium);
  color: black;
  list-style: none;

  /* Hide the caret, since we have our own custom one. This is done differently in Safari vs other browsers. */
  &::marker {
    display: none;
  }
  &::-webkit-details-marker {
    display: none;
  }

  ${Wrapper}[data-is-highlighted='true'] &::before {
    content: '';
    position: absolute;
    inset: -15px;
    background: hsl(200deg 100% 97%);
    animation: fadeIn 400ms;
    border-radius: 8px;
    box-shadow:
      0px 1px 2px hsl(200deg 100% 30% / 0.1),
      0px 3px 6px hsl(200deg 100% 30% / 0.1);
  }

  ${Wrapper}[data-is-highlighted='true'] &:focus-visible {
    outline: none;
  }
  ${Wrapper}[data-is-highlighted='true'] &:focus-visible::before {
    outline: 2px auto var(--color-primary);
    outline-offset: 0px;
  }
`;

const Answer = styled(animated.div)`
  font-size: 1rem;
  will-change: transform;
  padding-top: 0.5rem;
  padding-left: calc(2rem + 0.5rem);
  opacity: 0;

  &[data-status='open'] {
    animation: enterKeyframeAnimation
      cubic-bezier(0.215, 0.61, 0.355, 1) 600ms forwards;

    @keyframes enterKeyframeAnimation {
      from {
        transform: translateY(-10px);
        opacity: 0;
      }
      to {
        transform: translateY(0px);
        opacity: 1;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    &[data-status='open'] {
      animation: none;
    }
  }

  p,
  li {
    font-size: inherit;
  }
`;

const IconWrapper = styled.span`
  position: relative;
  flex-shrink: 0;
  display: grid;
  place-content: center;
  width: 2rem;
  height: 2rem;
`;

const Background = styled.span`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: var(--color-cloud-500);
  border-radius: 4px;
  transition:
    transform 250ms,
    background 250ms;

  @media (prefers-reduced-motion: no-preference) {
    ${Question}:hover & {
      transform: scale(1.125);
      transition: transform 150ms;
    }
  }
  ${Wrapper}[data-is-highlighted='true'] & {
    background: var(--color-warning-300);
    background: lch(85 112.86 83.18);
  }
`;

const Svg = styled.svg`
  position: relative;
`;

const QuestionText = styled.div`
  position: relative;
`;

export default FaqList;
